import { cx } from '@flowus/common/cx';
import { useState, type FC } from 'react';
import { Switch } from '@flowus/common/components/switch';
import { Images } from 'src/image';
import { Button } from '@flowus/common/components';
import { AiCoverStore, setAiCoverStore } from './store';
import { useOnceAnyDay } from '@flowus/common/hooks/use-once-any-day';
import { useOpenModal } from '@flowus/common/next-modal';
import { useOpenAskUpgradeSpace } from 'src/views/main/setting-modal/use-open-ask-upgrade-space';
import { useOpenUpgradeSpace } from 'src/views/main/setting-modal/use-open-setting-modal';
import { OpenSettingFrom } from 'src/views/main/setting-modal/type';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { extractCodeBlocks, getSpaceIconUrl, startGenerateAiCover } from './uitl';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useGetPageId } from 'src/utils/getPageId';
import { message } from '@flowus/common/components/message';
import { getCurrentSpace } from 'src/hooks/space';
// https://cdn2.flowus.cn/public/512d9d30-aa4c-43bc-b3f0-441b0b1a14be/amen.svg
const images = [
  Images.aiCover1,
  Images.aiCover2,
  Images.aiCover3,
  Images.aiCover4,
  Images.aiCover5,
  Images.aiCover6,
];
const coverItems: (CoverItemProps & { coverType: string })[] = [
  { coverType: '3:4', title: '3:4', description: '适合小红书封面' },
  { coverType: 'auto', title: '自由比例', description: '显示更多内容' },
];

interface Props {
  showQRCode?: boolean;
  showSpaceInfo?: boolean;
  chatId?: string;
}
export const AICoverAside: FC<Props> = (props) => {
  const [openQRCodeSwitch, setOpenQRCodeSwitch] = useState(true);
  const [openSpaceInfoSwitch, setOpenSpaceInfoSwitch] = useState(true);
  const [coverType, setCoverType] = useState(coverItems[0]?.coverType ?? 'auto');
  const html = AiCoverStore((state) => state.html);
  const loading = AiCoverStore((state) => state.loading);
  const { showOnce, close } = useOnceAnyDay('ai-cover-tip-1', {
    endDay: -1,
  });
  const pageId = useGetPageId();
  const openModal = useOpenModal();
  const isProSpace = useIsProSpace();
  const openAskUpgradeSpace = useOpenAskUpgradeSpace();
  const openUpgradeSpace = useOpenUpgradeSpace();

  const requestAiCover = async () => {
    if (loading) return;
    if (!isProSpace) {
      openAskUpgradeSpace(() => {
        openUpgradeSpace(OpenSettingFrom.ai_cover);
      });
      return;
    }
    const space = getCurrentSpace();
    const url = await getSpaceIconUrl(space.icon);
    setAiCoverStore({ loading: true });
    let parsm: any;
    if (props.chatId) {
      parsm = { chatId: props.chatId, spaceId: space.uuid };
    } else {
      parsm = {
        blockId: pageId,
        showQcode: openQRCodeSwitch,
        showSpaceInfo: openSpaceInfoSwitch,
        spaceId: getCurrentSpaceId(),
      };
      if (url) {
        parsm = { ...parsm, spaceIcon: url };
      }
    }
    try {
      await startGenerateAiCover(
        {
          ...parsm,
          ratio: coverType,
        },
        (resp) => {
          if (resp.message) {
            setAiCoverStore({ message: resp.message });
          } else if (resp.markdown) {
            void extractCodeBlocks(resp.markdown).then((res) => {
              if (res.length > 0) {
                setAiCoverStore({ loading: false, html: res[0]?.code });
              }
            });
          } else if (resp.error) {
            message.warning(resp.error);
            setAiCoverStore({ loading: false, message: '' });
          }
        }
      );
    } catch (err) {
      console.log('err: ', err);
      setAiCoverStore({ loading: false, html: '', message: '' });
    }
  };
  return (
    <div className="w-[300px] shrink-0">
      <div className="px-4 mt-4 mb-2 text-t2-medium">海报尺寸</div>
      <div className="px-4 flex items-center space-x-4">
        {coverItems.map((item) => {
          return (
            <CoverItem
              key={item.coverType}
              {...item}
              onSelect={() => {
                setCoverType(item.coverType);
              }}
              active={item.coverType === coverType}
            />
          );
        })}
      </div>
      <div
        hidden={props.showQRCode}
        onClick={() => {
          setOpenQRCodeSwitch(!openQRCodeSwitch);
        }}
        className={cx(
          'mt-2 flex justify-between items-center px-4 py-2.5',
          'animate-hover rounded-none animate-click'
        )}
      >
        <div className="text-t2">显示二维码</div>
        <Switch bgClassName="pointer-events-none" open={openQRCodeSwitch} />
      </div>
      <div
        hidden={props.showQRCode}
        onClick={() => {
          setOpenSpaceInfoSwitch(!openSpaceInfoSwitch);
        }}
        className={cx(
          'flex justify-between items-center px-4 py-2.5',
          'animate-hover rounded-none animate-click'
        )}
      >
        <div className="text-t2">显示空间头像、昵称</div>
        <Switch bgClassName="pointer-events-none" open={openSpaceInfoSwitch} />
      </div>
      <div className="text-normal text-t2-medium mt-4 ml-4">生成效果展示</div>
      <div className="grid grid-cols-3 px-4 mt-2 gap-2">
        {images.map((item, index) => {
          return <img key={index} className="w-[84px]" src={item} />;
        })}
      </div>
      <div className="px-4">
        <Button
          className="mt-6 mb-5 w-full"
          colorType="active"
          onClick={() => {
            if (html && showOnce) {
              openModal.warning({
                content: (
                  <div className="text-grey1">
                    <div>重新生成将覆盖原本的海报</div>
                    <div>请先保存海报，避免内容丢失</div>
                  </div>
                ),
                confirmText: '继续',
                confirm: () => {
                  close();
                  void requestAiCover();
                },
                cancelText: '取消',
              });
              return;
            }
            void requestAiCover();
          }}
        >
          生成海报
        </Button>
      </div>
    </div>
  );
};

interface CoverItemProps {
  title: string;
  description: string;
  active?: boolean;
  onSelect?: () => void;
}
const CoverItem: FC<CoverItemProps> = (props) => {
  return (
    <div
      className={cx(
        ' cursor-pointer rounded-lg w-[100px] h-[100px] flex flex-col justify-center items-center  border border-grey6',
        {
          'border-normal/85 border-[2px]': props.active,
        }
      )}
      onClick={props.onSelect}
    >
      <div className="w-[30px] h-[40px] bg-aside rounded" />
      <div className="mt-1 text-t4-medium text-normal">{props.title}</div>
      <div className="mt-0.5 text-t4 text-normal">{props.description}</div>
    </div>
  );
};
